import React, { useEffect } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { CallCenterViewModel } from '../../CallCenterViewModel'
import { useActivation } from 'hooks/mobx-hooks'
import isCallCenterEnabled from 'call-center/CallCenterFeatureToggle'
import { CallCenterMembershipItem } from '../../CallCenterSelectionViewModel'
import JobNotSupported from '../../components/JobNotSupported'
import Spinner from 'components/Spinner'
import Breadcrumb from 'components/Breadcrumb'
import styles from '../CallCenter.sass'
import { AddToWorkspaceOptions, InviteToWorkspaceOptions } from '../types'
import CustomerLookupSection from '../CustomerLookupSection'
import JobSection from '../JobSection'
import AddClientDialog from '../AddClientDialog'
import CreateJobConfirmationDialog from '../CreateJobConfirmationDialog/CreateJobConfirmationDialog'
import JobPricingSection from '../JobCheckout'
import { Route, Switch, useRouteMatch } from 'react-router'

interface Props extends RouteComponentProps<{ jobId?: string }> {
  vm: CallCenterViewModel
}

export default observer(function CallCenter({ vm, match: matchUrl }: Props) {
  useActivation(vm)

  const isOnCheckoutPage = Boolean(
    useRouteMatch('/call-center/:jobId/checkout'),
  )

  useEffect(() => {
    void vm.loadJobFromUrl(matchUrl.params.jobId)
  }, [matchUrl.params.jobId, vm])

  const handleInviteToWorkspace = ({
    contact,
    workspaceId,
    workspace,
  }: InviteToWorkspaceOptions) => {
    vm.addClientDialogVM.prefill({
      email: contact.email,
      firstName: contact.firstName,
      lastName: contact.lastName,
      phone: contact.phone,
      workspaceId,
      workspace,
    })

    vm.addClientDialogVM.show()
  }

  const handleAddToWorkspace = (options: AddToWorkspaceOptions) => {
    vm.addClientDialogVM.prefill({
      email: options.email,
      firstName: options.firstName,
      lastName: options.lastName,
      phone: options.phone,
    })

    vm.addClientDialogVM.show()
  }

  const handleCreateJob = (item: CallCenterMembershipItem) => {
    if (!item.workspace || !item.member) {
      return
    }

    vm.requestCreateJob({ workspace: item.workspace, member: item.member })
  }

  if (!isCallCenterEnabled()) {
    return <div>Call Center is not yet enabled.</div>
  }

  if (vm.loadJob.pending) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size={40} />
          <div>Loading job...</div>
        </div>
      </div>
    )
  }

  if (vm.loadJob.error) {
    return <JobNotSupported />
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.title}>Call center</div>
        <Breadcrumb
          items={vm.getBreadcrumbItems(isOnCheckoutPage).map((item) => ({
            ...item,
            onClick: () => vm.setActiveSection(item.id),
          }))}
        />
      </header>
      <div className={styles.headerSeparator} />

      <Switch>
        <Route exact path="/call-center">
          {vm.jobVM.hasActiveClient ? (
            <JobSection vm={vm.jobVM} />
          ) : (
            <CustomerLookupSection
              vm={vm.customerLookupVM}
              onAddClient={() => vm.addClientDialogVM.show()}
              onInviteToWorkspace={handleInviteToWorkspace}
              onAddToWorkspace={handleAddToWorkspace}
              onCreateJob={handleCreateJob}
            />
          )}
        </Route>

        <Route exact path="/call-center/:jobId">
          <JobSection vm={vm.jobVM} />
        </Route>

        <Route exact path="/call-center/:jobId/checkout">
          <JobPricingSection jobVm={vm.jobVM} />
        </Route>
      </Switch>

      <CreateJobConfirmationDialog vm={vm.jobVM.confirmationDialog} />
      <AddClientDialog vm={vm.addClientDialogVM} />
    </div>
  )
})
